import React, { useState, useEffect, useRef } from "react";
import { Typography, IconButton, TextField, Button, Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CodeSnippet from "./CodeSnippet";
import { EditorState, convertFromRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import Div from "@jumbo/shared/Div";
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from "react-sortable-hoc";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  AccordionDetailsStyled,
  AccordionStyled,
  AccordionSummaryStyled,
} from "../../components/mui/Accordions/StyledAccordion/StyleAccordion";
import CustomDragHandle from "components/CustomDragIcon";

let Editor = () => <React.Fragment />;

// Sortable algorithm item
const SortableAlgorithmItem = SortableElement(
  ({
    algorithm,
    algoIndex,
    translationIndex,
    expandedAlgoIndex,
    handleAlgoAccordionChange,
    handleAlgorithmTranslationChange,
    handleDeleteAlgorithmTranslation,
    handleAddCodeSnippet,
    handleCodeSnippetChange,
    handleDeleteCodeSnippet,
    programmingLanguages,
    editorStates,
    handleEditorStateChange,
  }) => (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        mb: 1,
        backgroundColor:
          expandedAlgoIndex === algoIndex ? "#f5f5f5" : "inherit",
        visibility: "visible !important",
        opacity: "1 !important",
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 1,
          px: 2,
          cursor: "pointer",
        }}
        onClick={() => {
          handleAlgoAccordionChange(algoIndex);
        }}
      >
        <CustomDragHandle />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {algorithm.algorithmName || `Algorithm ${algoIndex + 1}`}
        </Typography>
        <IconButton
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteAlgorithmTranslation(translationIndex, algoIndex);
          }}
        >
          <DeleteIcon />
        </IconButton>
        <ExpandMoreIcon
          sx={{
            transform:
              expandedAlgoIndex === algoIndex
                ? "rotate(180deg)"
                : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      {expandedAlgoIndex === algoIndex && (
        <Box sx={{ padding: "16px", borderTop: "1px solid #e0e0e0" }}>
          <TextField
            label="Algorithm Name"
            rows={1}
            value={algorithm.algorithmName || ""}
            onChange={(e) =>
              handleAlgorithmTranslationChange(
                translationIndex,
                algoIndex,
                "algorithmName",
                e.target.value
              )
            }
            sx={{ mb: 2, width: "100%" }}
          />
          <TextField
            label="Flow Chart Details"
            multiline
            rows={2}
            value={algorithm.flowChartDetails || ""}
            onChange={(e) =>
              handleAlgorithmTranslationChange(
                translationIndex,
                algoIndex,
                "flowChartDetails",
                e.target.value
              )
            }
            sx={{ mb: 2, width: "100%" }}
          />
          <Div sx={{ flex: 1, mb: 2, border: "1px solid lightgray" }}>
            <Editor
              editorStyle={{
                width: "100%",
                minHeight: 100,
                borderWidth: 1,
                borderTopStyle: "solid",
                borderColor: "lightgray",
              }}
              editorState={editorStates[algoIndex]}
              onEditorStateChange={(newState) =>
                handleEditorStateChange(algoIndex, newState)
              }
            />
          </Div>
          {algorithm.problemCodeSnippets.map((snippet, snippetIndex) => (
            <CodeSnippet
              key={snippetIndex}
              translationIndex={translationIndex}
              algoIndex={algoIndex}
              snippetIndex={snippetIndex}
              snippet={snippet}
              handleCodeSnippetChange={handleCodeSnippetChange}
              handleDeleteCodeSnippet={handleDeleteCodeSnippet}
              programmingLanguages={programmingLanguages}
            />
          ))}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleAddCodeSnippet(translationIndex, algoIndex)}
            sx={{ mt: 2 }}
          >
            Add Code Snippet
          </Button>
        </Box>
      )}
    </Box>
  )
);

// Sortable container for algorithm translations
const SortableAlgorithmsList = SortableContainer(
  ({
    algorithmTranslations,
    translationIndex,
    expandedAlgoIndex,
    handleAlgoAccordionChange,
    handleAlgorithmTranslationChange,
    handleDeleteAlgorithmTranslation,
    handleAddCodeSnippet,
    handleCodeSnippetChange,
    handleDeleteCodeSnippet,
    programmingLanguages,
    editorStates,
    handleEditorStateChange,
  }) => (
    <AccordionDetailsStyled>
      {algorithmTranslations.map((algorithm, algoIndex) => (
        <SortableAlgorithmItem
          key={`algo-${algoIndex}`}
          index={algoIndex}
          algorithm={algorithm}
          algoIndex={algoIndex}
          translationIndex={translationIndex}
          expandedAlgoIndex={expandedAlgoIndex}
          handleAlgoAccordionChange={handleAlgoAccordionChange}
          handleAlgorithmTranslationChange={handleAlgorithmTranslationChange}
          handleDeleteAlgorithmTranslation={handleDeleteAlgorithmTranslation}
          handleAddCodeSnippet={handleAddCodeSnippet}
          handleCodeSnippetChange={handleCodeSnippetChange}
          handleDeleteCodeSnippet={handleDeleteCodeSnippet}
          programmingLanguages={programmingLanguages}
          editorStates={editorStates}
          handleEditorStateChange={handleEditorStateChange}
        />
      ))}
    </AccordionDetailsStyled>
  )
);

const AlgorithmTranslationsAccordion = ({
  translationIndex,
  algorithmTranslations,
  handleAddAlgorithmTranslation,
  handleAlgorithmTranslationChange,
  handleDeleteAlgorithmTranslation,
  handleAddCodeSnippet,
  handleCodeSnippetChange,
  handleDeleteCodeSnippet,
  programmingLanguages,
}) => {
  const [editorStates, setEditorStates] = useState(() =>
    algorithmTranslations.map((algo) => {
      if (
        algo.algorithmTranslation &&
        typeof algo.algorithmTranslation === "string"
      ) {
        if (algo.algorithmTranslation.startsWith('{"blocks":')) {
          try {
            const rawContent = JSON.parse(algo.algorithmTranslation);
            return EditorState.createWithContent(convertFromRaw(rawContent));
          } catch (e) {
            console.error("Failed to parse algorithmTranslation:", e);
            return EditorState.createEmpty();
          }
        } else {
          try {
            const contentState = stateFromHTML(
              algo.algorithmTranslation || "<p></p>"
            );
            return EditorState.createWithContent(contentState);
          } catch (e) {
            console.error(
              "Failed to parse HTML content for algorithmTranslation:",
              e,
              algo.algorithmTranslation
            );
            return EditorState.createEmpty();
          }
        }
      }
      console.warn(
        `algorithmTranslation is missing or invalid for algo at index ${translationIndex}:`,
        algo
      );
      return EditorState.createEmpty();
    })
  );

  const [expandedAlgoIndex, setExpandedAlgoIndex] = useState(null);

  // Store previous algorithmTranslations
  const prevAlgorithmTranslationsRef = useRef(algorithmTranslations);

  useEffect(() => {
    Editor = require("react-draft-wysiwyg").Editor;
  }, []);

  // Update editorStates when algorithmTranslations changes
  useEffect(() => {
    setEditorStates((prevStates) => {
      const prevTranslations = prevAlgorithmTranslationsRef.current;

      const newEditorStates = algorithmTranslations.map((algo, index) => {
        const prevAlgo = prevTranslations[index];

        if (
          prevAlgo &&
          prevAlgo.algorithmTranslation === algo.algorithmTranslation &&
          index < prevStates.length
        ) {
          return prevStates[index] || EditorState.createEmpty();
        }

        if (
          algo.algorithmTranslation &&
          typeof algo.algorithmTranslation === "string"
        ) {
          if (algo.algorithmTranslation.startsWith('{"blocks":')) {
            try {
              const rawContent = JSON.parse(algo.algorithmTranslation);
              return EditorState.createWithContent(convertFromRaw(rawContent));
            } catch (e) {
              console.error("Failed to parse algorithmTranslation:", e);
              return EditorState.createEmpty();
            }
          } else {
            try {
              const contentState = stateFromHTML(
                algo.algorithmTranslation || "<p></p>"
              );
              return EditorState.createWithContent(contentState);
            } catch (e) {
              console.error(
                "Failed to parse HTML content for algorithmTranslation:",
                e,
                algo.algorithmTranslation
              );
              return EditorState.createEmpty();
            }
          }
        }
        console.warn(
          `algorithmTranslation is missing or invalid for algo at index ${index}:`,
          algo
        );
        return EditorState.createEmpty();
      });

      prevAlgorithmTranslationsRef.current = algorithmTranslations;

      return newEditorStates;
    });
  }, [algorithmTranslations]);

  const handleEditorStateChange = (algoIndex, newEditorState) => {
    setEditorStates((prevStates) =>
      prevStates.map((state, index) =>
        index === algoIndex ? newEditorState : state
      )
    );
    const contentState = newEditorState.getCurrentContent();
    const htmlContent = stateToHTML(contentState);
    handleAlgorithmTranslationChange(
      translationIndex,
      algoIndex,
      "algorithmTranslation",
      htmlContent
    );
  };

  const handleAlgoAccordionChange = (algoIndex) => {
    setExpandedAlgoIndex((prev) => (prev === algoIndex ? null : algoIndex));
  };

  // Handle drag-and-drop sort end
  const onSortEnd = ({ oldIndex, newIndex }) => {
    // Reorder algorithm translations
    const reorderedAlgorithms = arrayMove(
      algorithmTranslations,
      oldIndex,
      newIndex
    );
    // Reorder editor states to match
    const reorderedEditorStates = arrayMove(editorStates, oldIndex, newIndex);

    // Update parent state with reordered algorithms
    handleAlgorithmTranslationChange(
      translationIndex,
      -1,
      "reorder",
      reorderedAlgorithms
    );

    // Update local editor states
    setEditorStates(reorderedEditorStates);
  };

  return (
    <AccordionStyled defaultExpanded>
      <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ alignSelf: "center" }}>
          Algorithm Translations
        </Typography>
        <IconButton
          color="#475259"
          onClick={(event) => {
            event.stopPropagation();
            handleAddAlgorithmTranslation(translationIndex);
          }}
        >
          <AddIcon />
        </IconButton>
      </AccordionSummaryStyled>
      <SortableAlgorithmsList
        algorithmTranslations={algorithmTranslations}
        translationIndex={translationIndex}
        expandedAlgoIndex={expandedAlgoIndex}
        handleAlgoAccordionChange={handleAlgoAccordionChange}
        handleAlgorithmTranslationChange={handleAlgorithmTranslationChange}
        handleDeleteAlgorithmTranslation={handleDeleteAlgorithmTranslation}
        handleAddCodeSnippet={handleAddCodeSnippet}
        handleCodeSnippetChange={handleCodeSnippetChange}
        handleDeleteCodeSnippet={handleDeleteCodeSnippet}
        programmingLanguages={programmingLanguages}
        editorStates={editorStates}
        handleEditorStateChange={handleEditorStateChange}
        onSortEnd={onSortEnd}
        useDragHandle={true}
        axis="y"
        lockAxis="y"
      />
    </AccordionStyled>
  );
};

export default AlgorithmTranslationsAccordion;
